import { CommonWidgetProps } from "@/widget-components/types";
import { useContentHubFirehoseFeed } from "../shared/hooks/useContentHubFirehoseFeed";
import {
  CONTENT_HUB_SOURCE_GROUPS,
} from "@/features/content-hub";
import { FirehoseHighlightItem } from "../firehose-all/components";
import EmptyPlaceholder from "@/ui-components/empty-placeholder";
import { useFirehoseAdvancedSearchPanel } from "../shared/hooks/useFirehoseAdvancedSearchPanel";
import { BackendSource } from "@/shared/api/firehose";
import FilterHighlightPanels from "@/ui-components/FilterPanel/FilterHighlightPanels";
import { VirtuosoWrapper } from "@/ui-components/grouped-virtuoso";

const storyType = [
  "stocks-one",
  "stocks-two",
  "stocks-three",
  "stocks-four",
  "stocks-four-filings",
] as BackendSource[];

function StocksNews(props: CommonWidgetProps) {
  // Combined base feed from the content-hub adapter: one query per source
  // (The Fly, Benzinga, TradeXchange), merged newest-first; live stories via
  // the shared /v1/stream SSE connection.
  const {
    stories: stocksNewsStories,
    isLoading: isFeedLoading,
    loadNext: loadNextFeed,
    isLoadingMore: isLoadingMoreFeed,
    hasMore: hasMoreFeed,
  } = useContentHubFirehoseFeed(CONTENT_HUB_SOURCE_GROUPS.STOCKS_NEWS);

  const {
    filteredData: filteredStories,
    showSearchResults,
    loadNextPage: loadNextSearch,
    hasMore: hasMoreSearch,
    isSearching,
    // Filter panel
    filterSelections,
    setFilterSelections,
    isPanelOpen,
    closePanel,
    filterMenus,
    clearFilterSelections,
    // Highlight panel
    highlightSelections,
    setHighlightSelections,
    isHighlightPanelOpen,
    closeHighlightPanel,
    highlightMenus,
    clearHighlightSelections,
  } = useFirehoseAdvancedSearchPanel({
    storyType: storyType,
    originalData: stocksNewsStories,
    setTitle: props.headerOptions?.setTitle,
    defaultTitle: "Equities News",
    pushAction: props.headerOptions?.pushAction,
    persistKey: props.widgetCompositeId,
    closeSearch: props.headerOptions?.closeSearch,
    onSearchOpen: props.headerOptions?.onSearchOpen,
  });

  // Use search load functions if search is active, otherwise use feed load functions
  const loadNext = showSearchResults ? loadNextSearch : loadNextFeed;
  const isLoadingMore = showSearchResults ? isSearching : isLoadingMoreFeed;
  const hasMore = showSearchResults ? hasMoreSearch : hasMoreFeed;
  const isLoading = showSearchResults ? isSearching : isFeedLoading;

  return (
    <div className="h-full flex flex-col">
      {/* Filter Panel - full-screen library browser modal */}
      <FilterHighlightPanels
        filterMenus={filterMenus}
        filterSelections={filterSelections}
        setFilterSelections={setFilterSelections}
        isFilterOpen={isPanelOpen}
        onClearFilter={clearFilterSelections}
        onCloseFilter={closePanel}
        highlightMenus={highlightMenus}
        highlightSelections={highlightSelections}
        setHighlightSelections={setHighlightSelections}
        isHighlightOpen={isHighlightPanelOpen}
        onClearHighlight={clearHighlightSelections}
        onCloseHighlight={closeHighlightPanel}
      />

      <VirtuosoWrapper
        data={filteredStories}
        scrollManager={{
          enabled: true,
          notificationText: "New stories available",
          compareFirstItem: (a, b) => a?.[0]?.id === b?.[0]?.id,
        }}
        pagination={{
          hasMore,
          isLoadingMore,
          loadNext,
        }}
        renderItem={([data, portfolio]) => (
          <FirehoseHighlightItem
            item={data}
            highlightInfo={
              portfolio
                ? {
                    color: portfolio.color,
                    matchedTickers: [
                      ...(portfolio.tickers || []),
                      ...(portfolio.keywords || []),
                    ],
                    portfolioId: portfolio.id,
                    portfolioName: portfolio.name,
                  }
                : undefined
            }
          />
        )}
        renderEmpty={(isLoadingEmpty) => (
          <EmptyPlaceholder
            message="No stocks news available"
            isLoading={isLoadingEmpty || isLoading}
          />
        )}
      />
    </div>
  );
}

export default StocksNews;
